import React, { useCallback, useEffect, useMemo, useState } from 'react';

const ThemeContext = React.createContext(null);

const STORAGE_KEY = 'ao-theme';
const THEMES = ['dark', 'light'];

function getInitialTheme() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (THEMES.includes(stored)) {
      return stored;
    }
  }
  catch {
    // Storage unavailable (private mode / SSR) — fall through to default.
  }
  return 'dark';
}

function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    // Dark is the primary theme on :root; `.light` swaps the token ladder (global.css).
    document.documentElement.classList.toggle('light', theme === 'light');
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
    catch {
      // Persisting is best-effort.
    }
  }, [theme]);

  const setTheme = useCallback((value) => {
    if (THEMES.includes(value)) {
      setThemeState(value);
    }
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prevValue) => (prevValue === 'dark' ? 'light' : 'dark'));
  }, []);

  const contextValue = useMemo(() => ({
    theme,
    isLight: theme === 'light',
    setTheme,
    toggle,
  }), [setTheme, theme, toggle]);

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

ThemeProvider.displayName = 'ThemeProvider';
export { ThemeContext, ThemeProvider };
